import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import ReactGA from "react-ga4";

type ResetPasswordVariables = {
  email?: string;
  password1?: string;
  password2?: string;
  token?: string;
};

type ResetPasswordCallback = (value?: any) => void;

/**
 * Custom hook to reset the user password.
 * @param onCompleted
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{submit: Function, isSubmitting: boolean, resetSuccess: boolean}} - The function to submit the reset password request, the loading state, and the success state.
 */
export function useResetPassword(
  onCompleted: ResetPasswordCallback = () => {},
  onError: ResetPasswordCallback = () => {},
) {
  const [resetSuccess, setResetSuccess] = useState(null);

  const [comResetPassword, { loading }] = useMutation<any>(
    mutations.resetPassword,
  );

  const submit = useCallback(
    (variables: ResetPasswordVariables) => {
      try {
        comResetPassword({
          variables,
        })
          .then((result) => {
            const comResetPassword =
              result?.data?.resetPassword || result?.data?.comResetPassword;
            if (comResetPassword) {
              if (comResetPassword.success === false) {
                setResetSuccess(false);
                onError(comResetPassword.message);
              } else {
                onCompleted(result?.data);
              }
              setResetSuccess(true);
            }
          })
          .catch((error) => {
            setResetSuccess(false);
            onError(error instanceof Error ? error.message : String(error));
          });

        ReactGA.event({
          category: "Authentication",
          action: "PasswordReset",
          label: variables?.email.replace("@", "[at]"),
        });
      } catch (error) {
        onError(error instanceof Error ? error.message : String(error));
      }
    },
    [comResetPassword, onCompleted, onError],
  );

  return {
    submit,
    isSubmitting: loading,
    resetSuccess,
  };
}
